import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import CloseIcon from "@mui/icons-material/Close";
import { ThemeProvider } from "@mui/material/styles";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { ReactComponent as DeleteIcon } from "../Icons/Delete.svg";
import { ReactComponent as Settings } from "../Icons/Settings.svg";
import Table_theme from "../Themes/TableTheme";
import FormThemes from "../Themes/FormThemes";
import InventoryIcon from "@mui/icons-material/FilePresent";
import MainLayout from "../Themes/MainLayout";
import { validatePhone } from "../utils/phoneValidator";
import {
  CssBaseline,
  Box,
  Button,
  Grid,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Snackbar,
  Alert,
  MenuItem,
  Menu,
  Switch,
  Popover,
  CircularProgress,
  InputAdornment,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  OutlinedInput,
} from "@mui/material";
import { Add, Flag, Search, FilterList } from "@mui/icons-material";
import ClientProjectSubpage from "../Components/ClientProjectsDialog";
import debounce from "lodash/debounce";
import { useCurrentUser } from "../utils/useCurrentUser";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const clientFields = [
  "cl_name",
  "client_type",
  "industry_type",
  "status",
  "contact_person",
  "email",
  "website",
  "payment_terms",
  "federal_tax_id",
  "duns_number",
  "naics_code",
  "billing_email",
  "credit_limit",
  "phone_no",
  "alternate_phone_no",
  "country_id",
  "state_id",
  "city_id",
  "zip_id",
  "",
  "company_address",
  "billing_contact",
  "remarks",
];

const visibleClientFields = clientFields.filter(
  (f) => !["delete_flag"].includes(f)
);
const settingDisplayFields = [...clientFields];

const fieldLabels = {};
settingDisplayFields.forEach((field) => {
  if (field === "cl_id") fieldLabels[field] = "Client ID";
  else if (field === "cl_name") fieldLabels[field] = "Client Name";
  else if (field === "country_id") fieldLabels[field] = "Country";
  else if (field === "state_id") fieldLabels[field] = "State";
  else if (field === "city_id") fieldLabels[field] = "City";
  else if (field === "zip_id") fieldLabels[field] = "Zip Code";
  else if (field === "alternate_phone_no")
    fieldLabels[field] = "Alternate Phone No";
  else if (field === "federal_tax_id") fieldLabels[field] = "Federal Tax ID";
  else if (field === "duns_number") fieldLabels[field] = "DUNS Number";
  else if (field === "naics_code") fieldLabels[field] = "NAICS Code";
  else if (field === "billing_contact") fieldLabels[field] = "Billing Address";
  else
    fieldLabels[field] = field
      .replace(/_/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());
});

const ClientsPage = () => {
  const [projectStatus, setProjectStatus] = useState([]);
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [industryType, setIndustryType] = useState([]);
  const { userId, empId, boId, roles, hasPermission } = useCurrentUser();
  const [clients, setClients] = useState([]);

  const initialClientState = {
    bo_id: boId,
    cl_name: "",
    contact_person: "",
    email: "",
    phone_no: "",
    alternate_phone_no: "",
    company_address: "",
    country_id: "",
    state_id: "",
    city_id: "",
    zip_id: "",
    website: "",
    status: "",
    delete_flag: "",
    federal_tax_id: "",
    duns_number: "",
    naics_code: "",
    industry_type: "",
    client_type: "",
    billing_contact: "",
    billing_email: "",
    payment_terms: "",
    credit_limit: "",
    remarks: "",
  };
  const [newClient, setNewClient] = useState({ initialClientState });
  const [editClient, setEditClient] = useState({
    bo_id: boId,
    cl_name: "",
    contact_person: "",
    email: "",
    phone_no: "",
    alternate_phone_no: "",
    company_address: "",
    country_id: "",
    state_id: "",
    city_id: "",
    zip_id: "",
    website: "",
    status: "",
    delete_flag: "",
    federal_tax_id: "",
    duns_number: "",
    naics_code: "",
    industry_type: "",
    client_type: "",
    billing_contact: "",
    billing_email: "",
    payment_terms: "",
    credit_limit: "",
    remarks: "",
  });
  const requiredFields = [
    "cl_name",
    "contact_person",
    "email",
    "phone_no",
    "company_address",
  ];
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [visibleColumns, setVisibleColumns] = useState(clientFields); // Initialize without cl_id
  const [filterAnchors, setFilterAnchors] = useState({});
  const [filters, setFilters] = useState({ deleteFlag: "ACTIVE" });
  const [statusOptions, setStatusOptions] = useState([]);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [deleteClient, setDeleteClient] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [dropdownValues, setDropdownValues] = useState({
    country: [],
    state: [],
    city: [],
    zip_code: [],
    client_type: [],
    industry_type: [],
    payment_terms: [],
    status: [],
  });
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [openProjectDialog, setOpenProjectDialog] = useState(false);
  const [countryCodes, setCountryCodes] = useState([]);
  const rowsPerPage = 10;
  const handleOpenClientProjects = (cl_id) => {
    setSelectedClientId(cl_id);
    setOpenProjectDialog(true);
  };
  const handleCloseProjectDialog = () => {
    setOpenProjectDialog(false);
    setSelectedClientId(null);
  };
  // Fetch initial dropdown data
  useEffect(() => {
    const fetchInitialDropdowns = async () => {
      setDropdownLoading(true);
      try {
        const [clientTypeResponse, countryResponse] = await Promise.all([
          axios.get(`${BASE_URL}/clients/client-types`), // boId is not available in commonVariables. So here no need to pass boId.
          axios.get(`${BASE_URL}/commonVariables/countries`), // boId is not available in commonVariables.
        ]);
        const clientTypes = clientTypeResponse.data.client_type || [];
        const countries = countryResponse.data || [];

        if (!Array.isArray(clientTypes) || !Array.isArray(countries)) {
          throw new Error("Invalid dropdown data format received");
        }
        setDropdownValues((prev) => ({
          ...prev,
          client_type: clientTypes,
          country: countries.map((c) => ({
            id: c.country_id,
            name: c.country_name,
          })),
          state: [],
          city: [],
          zip_code: [],
        }));
        if (!clientTypes.length || !countries.length) {
          setSnackbarMsg(
            "No client types or countries available. Please contact the administrator."
          );
          setShowSnackbar(true);
        }
      } catch (error) {
        console.error("Error fetching initial dropdowns:", error);
        setSnackbarMsg(
          error.response?.data?.error || "Failed to load dropdown data."
        );
        setShowSnackbar(true);
      } finally {
        setDropdownLoading(false);
      }
    };
    fetchInitialDropdowns();
  }, []);

  const fetchDropdown = async (type, setter) => {
    try {
      const response = await fetch(`${BASE_URL}/commonVariables/${type}`);
      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      if (Array.isArray(data)) {
        setter(data); // directly set the array
      } else {
        throw new Error("Invalid dropdown data format received");
      }
    } catch (error) {
      console.error(`Error fetching ${type}:`, error);
      setSnackbarMsg(error.message || `Failed to load ${type}.`);
      setShowSnackbar(true);
    }
  };

  useEffect(() => {
    const loadStatusDropdown = async () => {
      // Load Industry Type
      await fetchDropdown("INDUSTRY_TYPE", (data) => {
        setDropdownValues((prev) => ({ ...prev, industry_type: data }));
        setIndustryType(data);
        console.log("setIndustryType:", data);
      });

      // Load Payment Terms
      await fetchDropdown("PAYMENT_TERMS", (data) => {
        setDropdownValues((prev) => ({ ...prev, payment_terms: data }));
        setPaymentTerms(data);
        console.log("setPaymentTerms", data);
      });
      await fetchDropdown("PROJECT_STATUS", (data) => {
        setDropdownValues((prev) => ({
          ...prev,
          status: data,
        }));
        setProjectStatus(data); // optional
      });
    };

    loadStatusDropdown();
  }, []);

  // Fetch dropdowns for edit dialog
  useEffect(() => {
    const loadDropdownsForEdit = async () => {
      if (
        !dialogOpen ||
        !isEditing ||
        !editClient.country_id ||
        isNaN(editClient.country_id)
      ) {
        setDropdownValues((prev) => ({
          ...prev,
          state: [],
          city: [],
          zip_code: [],
        }));
        return;
      }
      setDropdownLoading(true);
      try {
        const [statesRes, citiesRes, zipRes] = await Promise.all([
          axios.get(
            `${BASE_URL}/commonVariables/states/${editClient.country_id}`
          ),
          editClient.state_id && !isNaN(editClient.state_id)
            ? axios.get(
                `${BASE_URL}/commonVariables/cities/${editClient.state_id}`
              )
            : Promise.resolve({ data: [] }),
          editClient.city_id && !isNaN(editClient.city_id)
            ? axios.get(
                `${BASE_URL}/commonVariables/zipcodes/${editClient.city_id}`
              )
            : Promise.resolve({ data: [] }),
        ]);
        setDropdownValues((prev) => ({
          ...prev,
          state: Array.isArray(statesRes.data)
            ? statesRes.data.map((s) => ({
                id: s.state_id,
                name: s.state_name,
              }))
            : [],
          city: Array.isArray(citiesRes.data)
            ? citiesRes.data.map((c) => ({ id: c.city_id, name: c.city_name }))
            : [],
          zip_code: Array.isArray(zipRes.data)
            ? zipRes.data.map((z) => ({ id: z.zip_id, name: z.zip_code }))
            : [],
        }));
      } catch (error) {
        console.error("Error loading edit dropdowns:", error);
        setSnackbarMsg(
          error.response?.data?.error || "Failed to load edit dropdown data."
        );
        setShowSnackbar(true);
      } finally {
        setDropdownLoading(false);
      }
    };

    loadDropdownsForEdit();
  }, [
    dialogOpen,
    isEditing,
    editClient.country_id,
    editClient.state_id,
    editClient.city_id,
  ]);

  useEffect(() => {
    const fetchStatusFlags = async () => {
      try {
        const res = await fetch(`${BASE_URL}/commonVariables/statusFlag`);
        const data = await res.json();
        setStatusOptions(data); // [{ code: "ACTIVE", label: "Active" }, ...]
      } catch (err) {
        console.error("Failed to fetch employee status flags:", err);
      }
    };

    fetchStatusFlags();
  }, []);

  useEffect(() => {
    const fetchCountryCodes = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/commonVariables/countryCodes`);
        setCountryCodes(res.data);
      } catch (err) {
        console.error("Error fetching country codes:", err);
      }
    };

    fetchCountryCodes();
  }, []);

  const pageCount = Math.max(page + 1, Math.ceil(totalCount / rowsPerPage));

  const renderField = (field, value, mode = "new") => {
    const label = fieldLabels[field] || field;
    const isDropdown = [
      "country_id",
      "state_id",
      "city_id",
      "zip_id",
      "client_type",
      "status",
      "industry_type",
      "payment_terms",
    ].includes(field);

    const dropdownKey =
      field === "country_id"
        ? "country"
        : field === "state_id"
        ? "state"
        : field === "city_id"
        ? "city"
        : field === "zip_id"
        ? "zip_code"
        : field === "status"
        ? "status"
        : field === "status"
        ? "status"
        : field === "client_type"
        ? "client_type"
        : field === "industry_type"
        ? "industry_type"
        : "payment_terms";

    //  Unified onChange handler
    const handleChange = (e) => {
      const val = e.target.value;

      const updateClient = (prev) => {
        if (field === "country_id") {
          return {
            ...prev,
            country_id: val,
            state_id: "",
            city_id: "",
            zip_id: "",
          };
        } else if (field === "state_id") {
          return { ...prev, state_id: val, city_id: "", zip_id: "" };
        } else if (field === "city_id") {
          return { ...prev, city_id: val, zip_id: "" };
        } else {
          return { ...prev, [field]: val };
        }
      };

      if (isEditing) {
        setEditClient((prev) => updateClient(prev));
      } else {
        setNewClient((prev) => updateClient(prev));
      }

      // Clear error (no trim needed)
      if (errors[field]) {
        setErrors((prev) => {
          const { [field]: _, ...rest } = prev;
          return rest;
        });
      }
    };

    const commonSx = {
      "& .MuiInputBase-root": {
        fontSize: "14px",
        minHeight: 30,
        borderRadius: "9px",
        backgroundColor: "#fafafa",
        color: "#292727ff",
      },
      "& .MuiInputBase-input": {
        fontSize: "12px",
        padding: "8.5px 14px",
      },
      "& .MuiInputLabel-root": {
        fontSize: "14px",
      },
      "& .MuiSelect-select": {
        fontSize: "12px",
        padding: "8.5px 14px",
      },
      mt: 0.5,
      mb: 0,
      width: "200px",
    };
  // Dummy empty column (spacer only)
  if (!field) {
    return (
      <Grid
        item
        xs={12}
        sm={6}
         key="grid-spacer"
          sx={commonSx}
      />
    );
  }
    
    //  PHONE NUMBER FIELDS 
    if (field === "phone_no" || field === "alternate_phone_no") {
      const labelText =
        field === "phone_no" ? "Phone No *" : "Alternate Phone No";
      const phoneValue = value || "";
      const hasPhone = phoneValue.trim() !== "";

      const extractedCode =
        hasPhone && phoneValue.startsWith("+") && phoneValue.includes(" ")
          ? phoneValue.split(" ")[0]
          : "+91"; // UI default only

      const extractedPhone =
        hasPhone && phoneValue.startsWith("+") && phoneValue.includes(" ")
          ? phoneValue.split(" ").slice(1).join("")
          : "";

      return (
        <Grid item xs={12} sm={6} key={field}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              width: "100%",
            }}
          >
            {/* Code dropdown */}
            <FormControl
              size="small"
              sx={{
                width: "70px",
                mt: "4px",
                "& .MuiInputBase-root": {
                  ...commonSx["& .MuiInputBase-root"],
                  minHeight: "30px",
                },
              }}
            >
              <InputLabel shrink>Code</InputLabel>
              <Select
                value={extractedCode}
                label="Code"
                onChange={(e) => {
                  const newValue = `${e.target.value} ${extractedPhone}`;
                  isEditing
                    ? setEditClient((prev) => ({ ...prev, [field]: newValue }))
                    : setNewClient((prev) => ({ ...prev, [field]: newValue }));
                  setErrors((prev) => ({ ...prev, [field]: "" }));
                }}
                input={<OutlinedInput notched label="Code" />}
              >
                {countryCodes?.map((c) => (
                  <MenuItem key={c.code} value={c.label} sx={{ fontSize: 12 }}>
                    {c.label} {/* +91, +1, +971, ... */}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Phone number textbox */}
            <TextField
              size="small"
              fullWidth
              label={labelText}
              value={extractedPhone}
              onChange={(e) => {
                const phone = e.target.value.trim();
                const newValue = phone ? `${extractedCode} ${phone}` : "";
                isEditing
                  ? setEditClient((prev) => ({ ...prev, [field]: newValue }))
                  : setNewClient((prev) => ({ ...prev, [field]: newValue }));

                setErrors((prev) => ({ ...prev, [field]: "" }));
              }}
             InputLabelProps={{ shrink: true }}
              sx={{
                ...commonSx,
                width: "122px",
                "& .MuiFormHelperText-root": {
                  whiteSpace: "normal", // allow wrapping
                  marginLeft: 0,
                  lineHeight: 1.4,
                },
                "& .MuiInputBase-input": {
                  fontSize: "14px",
                  padding: "8px 12px",
                },
                "& .MuiInputLabel-root": { fontSize: "14px" },
              }}
            />
          </Box>
          {/* Error below row */}
          {errors[field] && (
            <Box
              sx={{
                mt: 0.5,
                maxWidth: "170px",
                ml: "5px",
                whiteSpace: "normal",
              }}
            >
              <Typography
                color="error"
                variant="caption"
                sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
              >
                {errors[field]}
              </Typography>
            </Box>
          )}
        </Grid>
      );
    }

    return (
      <Grid item xs={12} sm={6} key={field}>
        {isDropdown ? (
          <TextField
            select
            label={fieldLabels[field]}
            value={value || ""}
            onChange={handleChange} //  fixed
            size="small"
            InputLabelProps={{ shrink: true }} // forces label above
            sx={commonSx}
          >
            {dropdownKey === "status"
              ? dropdownValues.status.map((opt) => (
                  <MenuItem
                    sx={{ fontSize: "12px" }}
                    key={opt.variable_code}
                    value={opt.variable_code}
                  >
                    {opt.variable_value}
                  </MenuItem>
                ))
              : (dropdownValues[dropdownKey] || []).map((opt) => (
                  <MenuItem
                    sx={{ fontSize: "12px" }}
                    key={
                      field === "client_type"
                        ? opt.code
                        : opt.variable_code || opt.id
                    }
                    value={
                      field === "client_type"
                        ? opt.code
                        : opt.variable_code || opt.id
                    }
                  >
                    {field === "client_type"
                      ? `${opt.code} - ${opt.description}`
                      : opt.variable_value || opt.name}
                  </MenuItem>
                ))}
          </TextField>
        ) : (
          <TextField
            label={`${label}${requiredFields.includes(field) ? " *" : ""}`}
            value={value || ""}
            onChange={handleChange} //  fixed
            size="small"
            InputLabelProps={{ shrink: true }} // forces label above
            multiline={[
              "company_address",
              "billing_contact",
              "remarks",
            ].includes(field)}
            rows={3}
            sx={{
              ...commonSx,

              ...(["company_address", "billing_contact", "remarks"].includes(
                field
              )
                ? {
                    // 1) Make the textarea start at the top
                    "& .MuiInputBase-root": {
                      alignItems: "flex-start",
                      padding: 0, // remove default padding
                      whiteSpace: "normal",
                    },

                    // 2) Control text padding (this makes text start under label letter)
                    "& .MuiInputBase-input": {
                      padding: "8px", // same as your working page
                      overflow: "auto",
                    },

                    // 3) Adjust label position (critical!)
                    "& .MuiInputLabel-root": {
                      transform: "translate(14px, -9px) scale(0.80)",
                      transformOrigin: "top left",
                    },
                  }
                : {}),
            }}
            InputProps={{
              sx: ["company_address", "remarks"].includes(field)
                ? { overflow: "auto" } // scroll instead of auto-grow
                : {},
            }}
            error={Boolean(errors[field])} //  highlight red border
            helperText={errors[field] || ""} // show error text
            FormHelperTextProps={{
              sx:
                field === "credit_limit"
                  ? {
                      minHeight: "20px",
                      lineHeight: "1.2",
                      margin: 0,
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                    }
                  : {},
            }}
          />
        )}
      </Grid>
    );
  };

  const handleToggleColumn = (column) => {
    setVisibleColumns((prev) => {
      if (prev.includes(column)) {
        // Remove column
        return prev.filter((col) => col !== column);
      } else {
        // Add column back in original order
        return settingDisplayFields.filter(
          (field) => prev.includes(field) || field === column
        );
      }
    });
  };
  const handleSettingsClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleSettingsClose = () => {
    setAnchorEl(null);
  };

  const handleFilterIconClick = (field, event) => {
    setFilterAnchors((prev) => ({ ...prev, [field]: event.currentTarget }));
  };

  const handleFilterClose = (field) => {
    setFilterAnchors((prev) => ({ ...prev, [field]: null }));
  };
  const loadDropdowns = async (client, setClient) => {
    if (!client.country_id || isNaN(client.country_id)) {
      setDropdownValues((prev) => ({
        ...prev,
        state: [],
        city: [],
        zip_code: [],
      }));
      return;
    }
    setDropdownLoading(true);
    try {
      const [statesRes, citiesRes, zipRes] = await Promise.all([
        axios.get(`${BASE_URL}/commonVariables/states/${client.country_id}`),
        client.state_id && !isNaN(client.state_id)
          ? axios.get(`${BASE_URL}/commonVariables/cities/${client.state_id}`)
          : Promise.resolve({ data: [] }),
        client.city_id && !isNaN(client.city_id)
          ? axios.get(`${BASE_URL}/commonVariables/zipcodes/${client.city_id}`)
          : Promise.resolve({ data: [] }),
      ]);

      setDropdownValues((prev) => ({
        ...prev,
        state: Array.isArray(statesRes.data)
          ? statesRes.data.map((s) => ({ id: s.state_id, name: s.state_name }))
          : [],
        city: Array.isArray(citiesRes.data)
          ? citiesRes.data.map((c) => ({ id: c.city_id, name: c.city_name }))
          : [],
        zip_code: Array.isArray(zipRes.data)
          ? zipRes.data.map((z) => ({ id: z.zip_id, name: z.zip_code }))
          : [],
      }));
    } catch (error) {
      console.error("Error loading dropdowns:", error);
      setSnackbarMsg(
        error.response?.data?.error || "Failed to load dropdown data."
      );
      setShowSnackbar(true);
    } finally {
      setDropdownLoading(false);
    }
  };
  // For Add Client
  useEffect(() => {
    if (dialogOpen && !isEditing) {
      loadDropdowns(newClient, setNewClient);
    }
  }, [
    dialogOpen,
    isEditing,
    newClient.country_id,
    newClient.state_id,
    newClient.city_id,
  ]);

  // For Edit Client
  useEffect(() => {
    if (dialogOpen && isEditing) {
      loadDropdowns(editClient, setEditClient);
    }
  }, [
    dialogOpen,
    isEditing,
    editClient.country_id,
    editClient.state_id,
    editClient.city_id,
  ]);

  const fetchClientsFromDB = useCallback(async () => {
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        search: searchTerm,
        boId,
        empId,
        roles,
        ...filters,
      };
      const response = await axios.get(`${BASE_URL}/clients/paginated`, {
        params,
      });
      const clientsData = response.data.data || [];
      if (clientsData.length === 0) {
        setClients([]);
        setTotalCount(0);
        setSuccessMessage("No records found..");
        setTimeout(() => setSuccessMessage(""), 5000);
      } else {
        setClients(clientsData);
        setTotalCount(response.data.total || 0);
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
      setSnackbarMsg(error.response?.data?.error || "Failed to fetch clients.");
      setShowSnackbar(true);
    }
  }, [page, searchTerm, filters]);

  const debouncedFetchClients = useCallback(
    debounce(() => {
      fetchClientsFromDB();
    }, 500),
    [fetchClientsFromDB]
  );

  useEffect(() => {
    setPage(0); // reset to first page whenever searchTerm or filters change
  }, [searchTerm, filters]);

  useEffect(() => {
    debouncedFetchClients();
    return () => debouncedFetchClients.cancel();
  }, [debouncedFetchClients]);

  const handleAdd = () => {
    setIsEditing(false);
    setDialogOpen(true);
    setErrors({});
    setNewClient(initialClientState);
  };

  const handleDelete = (cl_id, cl_name) => {
    setDeleteId(cl_id);
    setDeleteClient(cl_name);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${BASE_URL}/clients/${boId}/${deleteId}`);
      await fetchClientsFromDB();
      setSuccessMessage("Record Deleted Successfully..");
      setTimeout(() => setSuccessMessage(""), 5000);
    } catch (error) {
      console.error("Error deleting client:", error);

      // Check if backend sent a custom message
      if (error.response && error.response.data && error.response.data.error) {
        setErrorMessage(error.response.data.error); // shows "linked to projects" message
      } else {
        setErrorMessage("Something went wrong");
      }
      setTimeout(() => setErrorMessage(""), 5000);
    } finally {
      setConfirmOpen(false);
      setDeleteId(null);
    }
  };
  const handleEditClick = async (client) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/clients/${client.bo_id}/${client.cl_id}`
      );
      const data = response.data;
      setEditClient({
        ...data,
        country_id: data.country || "",
        state_id: data.state || "",
        city_id: data.city || "",
        zip_id: data.zip_code || "",
      });
      setErrors({});
      setIsEditing(true);
      setDialogOpen(true);
    } catch (error) {
      console.error("Error fetching client for edit:", error);
      setSnackbarMsg(
        error.response?.data?.error || "Failed to load client data."
      );
      setShowSnackbar(true);
    }
  };
  const formatLocalDate = (date = new Date()) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
      date.getDate()
    )}`;
  };

  const formatLocalDateTime = (date = new Date()) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${formatLocalDate(date)} ${pad(date.getHours())}:${pad(
      date.getMinutes()
    )}:${pad(date.getSeconds())}`;
  };
  const normalizeNumeric = (v) => {
    if (v === "" || v === null || v === undefined) return null;
    return v;
  };
  const handleUpdateClient = async (clientData, isToggleFlag = false) => {
    if (!isToggleFlag) {
      const validationErrors = validateClient(clientData);
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return; // stop submission
      } else {
        setErrors({});
      }
    }
    setLoading(true);

    try {
      const payload = isToggleFlag
        ? {
            delete_flag: clientData.delete_flag,
            modified_at: formatLocalDateTime(),
            modified_by: "system",
            modified_on_date: formatLocalDate(),
          }
        : {
            ...clientData,
            credit_limit: normalizeNumeric(clientData.credit_limit),
            country: clientData.country_id,
            state: clientData.state_id,
            city: clientData.city_id,
            zip_code: clientData.zip_id,
            modified_at: formatLocalDateTime(),
            modified_by: empId,
            modified_on_date: formatLocalDate(),
          };

      await axios.put(
        `${BASE_URL}/clients/${boId}/${clientData.cl_id}`,
        payload
      );
      await fetchClientsFromDB();

      if (!isToggleFlag) {
        setDialogOpen(false);
        setIsEditing(false);
      }
      setSuccessMessage(
        `${
          isToggleFlag ? "Toggle flag updated" : "Client updated"
        } successfully..`
      );
      setTimeout(() => setSuccessMessage(""), 5000);
      // setShowSnackbar(true);
    } catch (error) {
      console.error(
        `Error ${isToggleFlag ? "toggling delete flag" : "updating client"}:`,
        error
      );
      setSnackbarMsg(
        error.response?.data?.error ||
          `Failed to ${isToggleFlag ? "toggle delete flag" : "update client"}.`
      );
      setShowSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const validateClient = (clientData) => {
    const errors = {};

    // Required fields
    if (!clientData.cl_name) errors.cl_name = "Client Name is required";
    if (!clientData.contact_person)
      errors.contact_person = "Contact Person is required";
    if (!clientData.email) errors.email = "Email is required";
    if (!clientData.phone_no) errors.phone_no = "Phone Number is required";
    if (!clientData.company_address)
      errors.company_address = "Company address is required";

    // Email format
    if (clientData.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(clientData.email)) {
        errors.email = "Invalid email format";
      }
    }
    // Phone number - Check total digits (7–15 digits)
    const phone1Result = validatePhone(clientData.phone_no);
    if (!phone1Result.isValid) {
      errors.phone_no = phone1Result.error;
    }
    // Alternate phone (optional — validate ONLY if filled)
    if (
      typeof clientData.alternate_phone_no === "string" &&
      clientData.alternate_phone_no.trim() !== ""
    ) {
      const phone2Result = validatePhone(clientData.alternate_phone_no);
      if (!phone2Result.isValid) {
        errors.alternate_phone_no = phone2Result.error;
      }
    }
    // Website
    if (clientData.website) {
      const websiteRegex =
        /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/.*)?$/;

      if (
        typeof clientData.website === "string" &&
        clientData.website.trim() !== ""
      ) {
        const website = clientData.website.trim();
        if (!websiteRegex.test(website)) {
          errors.website = "Invalid website address";
        }
      }
    }
   //billing email
    if (clientData.billing_email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (
        typeof clientData.billing_email === "string" &&
        clientData.billing_email.trim() !== ""
      ) {
        const billingEmail = clientData.billing_email.trim();
        if (!emailRegex.test(billingEmail)) {
          errors.billing_email = "Invalid billing email address";
        }
      }
    }
    // Credit limit (optional)
    if (
      typeof clientData.credit_limit === "string" &&
      clientData.credit_limit.trim() !== ""
    ) {
      const value = clientData.credit_limit.trim();

      const creditLimitRegex = /^\d{1,10}(\.\d{1,2})?$/;

      if (!creditLimitRegex.test(value)) {
        errors.credit_limit =
          "Invalid. Only numbers allowed, maximum 10 digits before decimal and 2 after.";
      } else {
        const numValue = Number(value);
        if (numValue >= 10000000000) {
          errors.credit_limit =
            "Credit limit must be less than 10,000,000,000.00";
        }
      }
    }

    // DUNS Number (optional)
    if (
      typeof clientData.duns_number === "string" &&
      clientData.duns_number.trim() !== ""
    ) {
      const duns = clientData.duns_number.trim();
      if (!/^\d{9}$/.test(duns)) {
        errors.duns_number = "DUNS number must be exactly 9 digits.";
      }
    }
    if (
      typeof clientData.naics_code === "string" &&
      clientData.naics_code.trim() !== ""
    ) {
      const naics = clientData.naics_code.trim();
      const naicsRegex = /^\d{2,6}$/;
      if (!naicsRegex.test(naics)) {
        errors.naics_code = "Invalid NAICS code. Must be 2 to 6 digits.";
      }
    }
    // Federal Tax ID (EIN) Validation (optional)
    if (
      typeof clientData.federal_tax_id === "string" &&
      clientData.federal_tax_id.trim() !== ""
    ) {
      const ein = clientData.federal_tax_id.trim();
      const einRegex = /^[0-9]{2}-[0-9]{7}$/;

      if (!einRegex.test(ein)) {
        errors.federal_tax_id = "Invalid. Format must be NN-NNNNNNN.";
      }
    }
    return errors;
  };

  const handleToggleFlag = (client) => {
    const updatedClient = {
      ...client,
      delete_flag: client.delete_flag === "Y" ? "N" : "Y",
    };
    handleUpdateClient(updatedClient, true);
  };

  const handleAddClient = async () => {
    const newErrors = validateClient(newClient);
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return; // stop submission
    } else {
      setErrors({});
    }

    try {
      const payload = {
        ...newClient,
        credit_limit: normalizeNumeric(newClient.credit_limit),
        country: newClient.country_id,
        state: newClient.state_id,
        city: newClient.city_id,
        zip_code: newClient.zip_id,
        delete_flag: "N",
        data_source: "UI",
        created_by: empId,
        created_on_date: formatLocalDate(),
        created_at: formatLocalDateTime(),
        modified_by: empId,
        modified_on_date: formatLocalDate(),
        modified_at: formatLocalDateTime(),
      };
      await axios.post(`${BASE_URL}/clients`, payload);
      await fetchClientsFromDB();
      setNewClient({
        bo_id: boId,
        cl_name: "",
        contact_person: "",
        email: "",
        phone_no: "",
        alternate_phone_no: "",
        company_address: "",
        country_id: "",
        state_id: "",
        city_id: "",
        zip_id: "",
        website: "",
        status: "",
        federal_tax_id: "",
        duns_number: "",
        naics_code: "",
        industry_type: "",
        client_type: "",
        billing_contact: "",
        billing_email: "",
        payment_terms: "",
        credit_limit: "",
        remarks: "",
      });
      // setOpen(false);
      setDialogOpen(false);
      setIsEditing(false);
      setSuccessMessage("Client added successfully!");
      setTimeout(() => setSuccessMessage(""), 5000);
    } catch (error) {
      console.error("Error adding client:", error);
      setSnackbarMsg(error.response?.data?.error || "Failed to add client.");
      setShowSnackbar(true);
    }
  };
  return (
    <ThemeProvider theme={Table_theme}>
      <CssBaseline />
      <MainLayout>
        <Typography variant="h4" gutterBottom fontSize="25px" fontWeight="600">
          Clients
        </Typography>
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}

        <Box display="flex" alignItems="center" mb={2} gap={1.5}>
          <Button onClick={handleSettingsClick} sx={{ minWidth: 0, p: 1 }}>
            <Settings />
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleSettingsClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            PaperProps={{
              sx: {
                mt: 1,
                p: 1,
                width: 240,
                maxHeight: 400,
                overflowY: "auto",
                borderRadius: 2,
              },
            }}
          >
            {settingDisplayFields.map((field) => {
              if (!field) return null; 
                return (
              <MenuItem key={field} disableGutters sx={{ p: 0, mb: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                    border: "1px solid #ccc",
                    borderRadius: 1.5,
                    px: 2,
                    py: 1,
                    backgroundColor: "#fff",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.8rem",
                      color: "#333",
                      fontWeight: "bold",
                    }}
                  >
                    {fieldLabels[field] || field}
                  </Typography>
                  <Switch
                    size="small"
                    checked={visibleColumns.includes(field)}
                    onChange={() => handleToggleColumn(field)}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "#2DB0E4",
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        { backgroundColor: "#2DB0E4" },
                    }}
                  />
                </Box>
              </MenuItem>
);
})}
          </Menu>
          {hasPermission("Clients", "Edit") && (
            <Button
              variant="contained"
              size="small"
              onClick={handleAdd}
              sx={{
                fontWeight: "bold",
                borderRadius: "8px",
                px: 2.5,
                height: 36,
                backgroundColor: "#2DB0E4",
              }}
              startIcon={<Add />}
            >
              Add
            </Button>
          )}
          <TextField
            placeholder="Search..."
            size="small"
            sx={{
              width: 250,
              "& input": { fontSize: 14 }, // input + placeholder
              "& input::placeholder": { fontSize: 12 }, // placeholder only
            }}
            InputProps={{
              sx: {
                height: 36, // Reduce overall input height
                paddingRight: 0,
              },
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {/* Status Dropdown */}
          <TextField
            select
            size="small"
            label="Status"
            value={filters.deleteFlag || ""}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, deleteFlag: e.target.value }))
            }
            sx={{
              width: 200,
              // Make height match the 36px of the search field
              "& .MuiInputBase-root": {
                height: 36,
              },

              // Fix vertical alignment of text
              "& .MuiSelect-select": {
                display: "flex",
                alignItems: "center",
                fontSize: 12,
                padding: "0 14px",
              },

              // Label font size
              "& .MuiInputLabel-root": { fontSize: "14px" },
            }}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  sx: {
                    "& .MuiMenuItem-root": {
                      fontSize: "14px",
                      minHeight: "32px",
                    },
                  },
                },
              },
            }}
          >
            <MenuItem value="All">All</MenuItem>
            {statusOptions.map((opt) => (
              <MenuItem key={opt.code} value={opt.code}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Box
          sx={{ width: "100%", overflowX: "auto", px: 0, py: 2, mt: "-10px" }}
        >
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ textAlign: "center" }}>
                    <strong>Actions</strong>
                  </TableCell>
                  {visibleColumns.map((field) => (
                    <TableCell key={field}>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <strong>{fieldLabels[field]}</strong>
                        <IconButton
                          size="small"
                          onClick={(e) => handleFilterIconClick(field, e)}
                        >
                          <FilterList fontSize="small" />
                        </IconButton>
                        <Popover
                          open={Boolean(filterAnchors[field])}
                          anchorEl={filterAnchors[field]}
                          onClose={() => handleFilterClose(field)}
                          anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "left",
                          }}
                        >
                          <Box p={2}>
                            <TextField
                              label={`Filter ${fieldLabels[field]}`}
                              size="small"
                              value={filters[field] || ""}
                              onChange={(e) =>
                                setFilters((prev) => ({
                                  ...prev,
                                  [field]: e.target.value,
                                }))
                              }
                            />
                            <Box
                              mt={1}
                              display="flex"
                              justifyContent="flex-end"
                              gap={1}
                            >
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => handleFilterClose(field)}
                              >
                                Apply
                              </Button>
                              <Button
                                size="small"
                                onClick={() => {
                                  setFilters((prev) => ({
                                    ...prev,
                                    [field]: "",
                                  }));
                                  handleFilterClose(field);
                                }}
                              >
                                Clear
                              </Button>
                            </Box>
                          </Box>
                        </Popover>
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={visibleColumns.length + 1}
                      align="center"
                    >
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : clients.length > 0 ? (
                  clients.map((client) => (
                    <TableRow key={client.cl_id}>
                      <TableCell>
                        <Box display="flex" gap={0.5}>
                          <Tooltip
                            title={
                              hasPermission("Clients", "Edit")
                                ? "Edit"
                                : "No permission"
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  if (hasPermission("Clients", "Edit")) {
                                    handleEditClick(client);
                                  }
                                }}
                                disabled={!hasPermission("Clients", "Edit")}
                                sx={{
                                  color: hasPermission("Clients", "Edit")
                                    ? "#2DB0E4"
                                    : "#ccc",
                                }}
                              >
                                <EditOutlinedIcon sx={{ fontSize: "16px" }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip
                            title={
                              hasPermission("Clients", "Delete")
                                ? "Delete"
                                : "No permission"
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  hasPermission("Clients", "Delete") &&
                                  handleDelete(client.cl_id, client.cl_name)
                                }
                                disabled={!hasPermission("Clients", "Delete")}
                                sx={{
                                  color: hasPermission("Clients", "Delete")
                                    ? "#fc0404ff"
                                    : "#ccc",
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip
                            title={
                              hasPermission("Clients", "Edit")
                                ? "Toggle Delete Flag"
                                : "No permission"
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  hasPermission("Clients", "Edit") &&
                                  handleToggleFlag(client)
                                }
                                disabled={!hasPermission("Clients", "Edit")}
                              >
                                <Flag
                                  style={{
                                    color:
                                      client.delete_flag === "N"
                                        ? "#2E9F50"
                                        : "#FF0C0C",
                                    transition: "color 0.2s",
                                    width: 17,
                                    height: 17,
                                    verticalAlign: "middle",
                                  }}
                                />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip
                            title={
                              hasPermission("Clients", "View")
                                ? "Client Projects"
                                : "No permission"
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleOpenClientProjects(client.cl_id)
                                }
                              >
                                <InventoryIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      {visibleColumns.map((field) => (
                        <TableCell key={field}>
                          {field === "country_id"
                            ? client.country
                            : field === "state_id"
                            ? client.state
                            : field === "city_id"
                            ? client.city
                            : field === "zip_id"
                            ? client.zip_code
                            : client[field]}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={visibleColumns.length + 1}
                      align="center"
                    >
                      No matching data found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box display="flex" justifyContent="center" mt={2}>
            <Pagination
              //  count={Math.ceil(totalCount / rowsPerPage)}
              count={pageCount}
              page={page + 1}
              onChange={(_, newPage) => setPage(newPage - 1)}
              color="primary"
              variant="outlined"
              shape="rounded"
              sx={{
                "& .MuiPaginationItem-root": {
                  borderRadius: "8px",
                  minWidth: "32px",
                  height: "32px",
                  fontSize: "14px",
                },
                "& .Mui-selected": {
                  backgroundColor: "#b3dbea !important",
                  color: "black",
                  fontWeight: "bold",
                },
                "& .MuiPaginationItem-ellipsis": { display: "none" },
              }}
            />
          </Box>
          <ThemeProvider theme={FormThemes}>
            <CssBaseline />
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
              <DialogTitle>Confirm Deletion</DialogTitle>
              <DialogContent>
                {`Are you sure you want to permanently delete client ${deleteClient}?`}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
                <Button
                  onClick={confirmDelete}
                  color="error"
                  variant="contained"
                >
                  Delete
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog
              open={dialogOpen}
              onClose={() => {
                setDialogOpen(false);
                setIsEditing(false);
              }}
              fullWidth
              maxWidth="sm"
              sx={{
                "& .MuiDialog-paper": {
                  borderRadius: 5,
                  overflow: "visible",
                  width: "920px", // adjust for your 2-column layout width: "500px"
                  maxWidth: "95vw", // still responsive
                  margin: "0 auto", // ensure centering
                },
              }}
            >
              <DialogTitle
                sx={{
                  borderRadius: "12px",
                  bgcolor: "#f5f5f5",
                  p: 1,
                  fontSize: "16px",
                  fontWeight: 600,
                  //m: 0,
                }}
              >
                {isEditing
                  ? `Update Client: ${editClient.cl_name}`
                  : "Add Client"}
                <IconButton
                  aria-label="close"
                  onClick={() => {
                    setDialogOpen(false);
                    setIsEditing(false);
                  }}
                  sx={{ position: "absolute", right: 8, top: 5 }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent
                dividers
                sx={{
                  p: "14px 28px",
                  display: "flex",
                  flexDirection: "column",
                  // alignItems: "center", // Center horizontally
                  alignItems: "stretch",
                  backgroundColor: "#fff",
                  mt: 0,
                }}
              >
                <Grid
                  container
                  rowSpacing={2}
                  columnSpacing={1}
                  sx={{ width: "100%", p: 1 }}
                >
                  {visibleClientFields.map((field, index) => (
                    <Grid item xs={12} sm={3} key={index}>
                      {renderField(
                        field,
                        isEditing ? editClient[field] : newClient[field],
                        isEditing
                          ? (e) =>
                              setEditClient({
                                ...editClient,
                                [field]: e.target.value,
                              })
                          : (e) =>
                              setNewClient({
                                ...newClient,
                                [field]: e.target.value,
                              })
                      )}
                    </Grid>
                  ))}
                </Grid>
              </DialogContent>
              <DialogActions sx={{ p: "8px 24px", justifyContent: "flex-end" }}>
                <Button
                  onClick={() => {
                    setDialogOpen(false);
                    setIsEditing(false);
                    setErrors({}); // clear validation errors
                  }}
                  variant="outlined"
                  color="inherit"
                  sx={{ mr: 1, color: "#555", borderColor: "#ccc" }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={
                    isEditing
                      ? () => handleUpdateClient(editClient)
                      : handleAddClient
                  }
                  variant="contained"
                  sx={{
                    fontWeight: "bold",
                    borderRadius: "8px",
                    px: 2.5,
                    height: 36,
                    ...(isEditing
                      ? {
                          backgroundColor: "#4CAF50",
                          "&:hover": { backgroundColor: "#45a049" },
                        }
                      : {
                          backgroundColor: "#2DB0E4",
                          "&:hover": { backgroundColor: "#1D87B0" },
                        }),
                  }}
                >
                  {isEditing ? "Update" : "Save"}
                </Button>
              </DialogActions>
            </Dialog>
          </ThemeProvider>
          <ClientProjectSubpage
            open={openProjectDialog}
            onClose={handleCloseProjectDialog}
            cl_id={selectedClientId}
          />
          <Snackbar
            open={showSnackbar}
            autoHideDuration={3000}
            onClose={() => setShowSnackbar(false)}
            message={snackbarMsg}
          >
            <Alert
              onClose={() => setShowSnackbar(false)}
              severity={snackbarSeverity} // now dynamic
              sx={{ width: "100%" }}
            >
              {snackbarMsg}
            </Alert>
          </Snackbar>
        </Box>
      </MainLayout>
    </ThemeProvider>
  );
};

export default ClientsPage;
